import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";

const API = "/api/auditoria/revision";

const authConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  },
});

const formatearFechaHora = (valor) => {
  if (!valor) return "—";
  const fecha = new Date(valor);
  return Number.isNaN(fecha.getTime())
    ? "—"
    : fecha.toLocaleString("es-CO");
};

const nombreInstructor = (instructor) =>
  [
    instructor?.nombre1Instructor,
    instructor?.nombre2Instructor,
    instructor?.apellido1Instructor,
    instructor?.apellido2Instructor,
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim() || "—";

const claseEstado = (estado) => {
  const mapa = {
    "Requiere ajustes": "badge bg-warning text-dark",
    Aprobado: "badge bg-success",
    Rechazado: "badge bg-danger",
  };

  return mapa[estado] || "badge bg-secondary";
};

const Auditoria_ListarRevision = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const documentoFiltro = searchParams.get("documentoId") || "";

  const [revisiones, setRevisiones] = useState([]);
  const [resultado, setResultado] = useState("");
  const [buscar, setBuscar] = useState("");
  const [loading, setLoading] = useState(false);

  const manejarError = useCallback(
    (error, mensaje) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        Swal.fire(
          "Sesión expirada",
          "Inicia sesión nuevamente.",
          "warning"
        ).then(() => navigate("/login"));
        return;
      }

      Swal.fire(
        "Error",
        error?.response?.data?.message || mensaje || "Ocurrió un error.",
        "error"
      );
    },
    [navigate]
  );

  const cargar = useCallback(async () => {
    try {
      setLoading(true);

      const url = documentoFiltro
        ? `${API}/documento/${documentoFiltro}`
        : API;

      const { data } = await axios.get(url, authConfig());

      setRevisiones(
        Array.isArray(data?.revisiones) ? data.revisiones : []
      );
    } catch (error) {
      manejarError(error, "No se pudieron cargar las revisiones.");
    } finally {
      setLoading(false);
    }
  }, [documentoFiltro, manejarError]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const filtrados = useMemo(() => {
    const texto = buscar.trim().toLowerCase();

    return revisiones.filter((revision) => {
      const auditoria = revision.auditoriaDocumentoObjectId || {};
      const instructor = auditoria.instructorObjectId || {};
      const tipo = auditoria.tipoDocumentoObjectId || {};

      const contenido = [
        nombreInstructor(instructor),
        instructor.documentoInstructor,
        tipo.nombreTipoDocumento,
        revision.resultadoRevision,
        revision.conceptoAuditor,
        revision.observacionRevision,
        revision.numeroRevision,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return (
        (!texto || contenido.includes(texto)) &&
        (!resultado || revision.resultadoRevision === resultado)
      );
    });
  }, [revisiones, buscar, resultado]);

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
        <div>
          <h4 className="mb-1">Historial de auditoría</h4>
          <p className="text-muted mb-0">
            Revisiones finalizadas. Estos registros son de consulta y no se editan.
          </p>
        </div>

        <div className="d-flex gap-2">
          {documentoFiltro && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() =>
                navigate(`/auditoria/documento/revisar/${documentoFiltro}`)
              }
            >
              <i className="fa-solid fa-file-magnifying-glass me-2"></i>
              Ver expediente
            </button>
          )}

          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={cargar}
            disabled={loading}
          >
            <i className="fa-solid fa-rotate me-2"></i>
            Actualizar
          </button>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-8">
              <label className="form-label">Buscar</label>
              <input
                type="text"
                className="form-control"
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
                placeholder="Instructor, documento, resultado, concepto..."
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Resultado</label>
              <select
                className="form-select"
                value={resultado}
                onChange={(e) => setResultado(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="Aprobado">Aprobado</option>
                <option value="Requiere ajustes">Requiere ajustes</option>
                <option value="Rechazado">Rechazado</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Instructor</th>
                  <th>Documento</th>
                  <th>Fecha</th>
                  <th>Resultado</th>
                  <th>Concepto</th>
                  <th className="text-end">Acción</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <div className="spinner-border spinner-border-sm me-2" />
                      Cargando historial...
                    </td>
                  </tr>
                ) : filtrados.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      No hay revisiones para mostrar.
                    </td>
                  </tr>
                ) : (
                  filtrados.map((revision) => {
                    const auditoria = revision.auditoriaDocumentoObjectId || {};
                    const instructor = auditoria.instructorObjectId || {};
                    const tipo = auditoria.tipoDocumentoObjectId || {};

                    return (
                      <tr key={revision._id}>
                        <td>{revision.numeroRevision}</td>
                        <td>
                          <strong>{nombreInstructor(instructor)}</strong>
                          <div className="small text-muted">
                            {instructor.documentoInstructor || "—"}
                          </div>
                        </td>
                        <td>{tipo.nombreTipoDocumento || "—"}</td>
                        <td>{formatearFechaHora(revision.fechaRevision)}</td>
                        <td>
                          <span className={claseEstado(revision.resultadoRevision)}>
                            {revision.resultadoRevision}
                          </span>
                        </td>
                        <td>{revision.conceptoAuditor || "—"}</td>
                        <td className="text-end">
                          {auditoria._id && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() =>
                                navigate(
                                  `/auditoria/documento/revisar/${auditoria._id}`
                                )
                              }
                            >
                              <i className="fa-solid fa-eye me-2"></i>
                              Ver expediente
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auditoria_ListarRevision;
